import React, { useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import Slider from 'react-slick'
import { useMediaQuery } from 'react-responsive'
import useStretchConfig from './config'
import { useTranslation } from '../../lib/i18n'
import { NextButton, PreviousButton } from './arrow_button'
import { ClientOnly, useIsHydrated, useSlickA11yFix } from '../../util/ssr'
import { FaChevronCircleUp, FaChevronCircleDown } from 'react-icons/fa'

const Wrapper = styled.div`
    overflow-x: hidden; /** Weird IE bug */
    position: relative;
`
const ImageWrapper = styled.div`
    cursor: move;

    margin-top: 6px;
    margin-bottom: 6px;

    img {
        border-radius: 8px;
    }
`

const Mask = styled.div`
    z-index: 1;
    width: 100%;
    height: calc(100% / 3);
    pointer-events: none;
    position: absolute;

    ${(props) =>
        props.top
            ? css`
                  top: 0px;
              `
            : css`
                  bottom: 0px;
              `}

    background: rgb(255, 255, 255);
    background: linear-gradient(
        ${(props) => (props.top ? 180 : 0)}deg,
        rgba(255, 255, 255, 1) 40%,
        rgba(255, 255, 255, 0.5) 100%
    );
`

const defaultSliderProps = {
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    arrows: false
}

const sliderPropsVertical = {
    ...defaultSliderProps,
    vertical: true,
    verticalSwiping: true,
    slidesToShow: 3
}

const sliderPropsHorizontal = {
    ...defaultSliderProps,
    slidesToShow: 1
}

export default ({ sliderIndex, setSliderIndex }) => {
    const sliderRef = useRef()
    const isHydrated = useIsHydrated()
    const slickA11yRef = useSlickA11yFix()
    const scrollDirectionVertical = useMediaQuery({ minWidth: 950 })
    const config = useStretchConfig()
    const { t } = useTranslation(undefined, { keyPrefix: 'home.stretchesSection' })

    const largeConfig = [config[config.length - 1], ...config.slice(0, config.length - 1)]
    const configuration = scrollDirectionVertical ? largeConfig : config

    useEffect(() => {
        if (isHydrated && sliderRef.current) {
            sliderRef.current.slickGoTo(sliderIndex)
        }
    }, [sliderIndex, isHydrated])

    useEffect(() => {
        if (isHydrated && sliderRef.current) {
            setTimeout(() => sliderRef.current.innerSlider.onWindowResized(), 1000)
        }
    }, [isHydrated])

    const staticFallback = (
        <Wrapper>
            <ImageWrapper>
                <img width={640} height={360} src={config[sliderIndex]?.image || config[0]?.image} alt={config[sliderIndex]?.title || config[0]?.title} />
            </ImageWrapper>
        </Wrapper>
    )

    return (
        <ClientOnly fallback={staticFallback}>
            <Wrapper ref={slickA11yRef}>
                <PreviousButton
                    aria-label={t('previous')}
                    vertical={scrollDirectionVertical}
                    onClick={() => sliderRef.current.slickPrev()}
                    className="button clear primary"
                >
                    <FaChevronCircleUp className="arrow-icon box-shadow" />
                </PreviousButton>
                {scrollDirectionVertical && <Mask top={true} />}
                <Slider
                    beforeChange={(prev, next) => {
                        setTimeout(() => setSliderIndex(next))
                    }}
                    ref={sliderRef}
                    {...(scrollDirectionVertical ? sliderPropsVertical : sliderPropsHorizontal)}
                >
                    {configuration.map((config, index) => (
                        <ImageWrapper key={index}>
                            <img width={640} height={360} src={config.image} alt={config.title} />
                        </ImageWrapper>
                    ))}
                </Slider>
                {scrollDirectionVertical && <Mask />}
                <NextButton
                    aria-label={t('next')}
                    vertical={scrollDirectionVertical}
                    onClick={() => sliderRef.current.slickNext()}
                    className="button clear primary"
                >
                    <FaChevronCircleDown className="arrow-icon box-shadow" />
                </NextButton>
            </Wrapper>
        </ClientOnly>
    )
}
